import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import type { Traveler } from "@b2bportal/traveler-api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { useTravelerDelete } from "@tenant/traveler";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogTrigger,
  Button,
  ButtonTypeEnum,
} from "@tenant/ui";
import defaultStyles from "./TravelerDelete.module.scss";
import { useTravelerStyles } from "~core-traveler/themes";
import { CoreTravelerComponent } from "~core-traveler/types";

export type TravelerDeleteProps = PlatformComponentProps & {
  traveler: Traveler;
  trigger?: React.ReactElement;
  onDelete?: (traveler: Traveler) => void;
};

const COMPONENT_KEY = CoreTravelerComponent.TravelerDelete;

export const TravelerDelete = ({ trigger, ...props }: TravelerDeleteProps) => {
  const styles = useTravelerStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  const { name, handleOnDelete } = useTravelerDelete(props);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {trigger || (
          <Button className={cn("trigger")} buttonType={ButtonTypeEnum.Primary}>
            {t("core-traveler.actions.delete.button")}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent
        className={cn("content")}
        title={t("core-traveler.actions.delete.alert.title")}
        status="warning"
        description={
          <Trans
            i18nKey="core-traveler.actions.delete.alert.description"
            values={{ name }}
          />
        }
        actions={
          <>
            <AlertDialogAction asChild>
              <Button
                className={cn("delete")}
                buttonType={ButtonTypeEnum.Primary}
                onClick={() => handleOnDelete()}
              >
                {t("core-traveler.actions.delete.alert.actions.confirm")}
              </Button>
            </AlertDialogAction>
            <AlertDialogCancel asChild>
              <Button
                className={cn("cancel")}
                buttonType={ButtonTypeEnum.Tertiary}
              >
                {t("core-traveler.actions.delete.alert.actions.cancel")}
              </Button>
            </AlertDialogCancel>
          </>
        }
      />
    </AlertDialog>
  );
};
